/**
 * SOLFIE — Cart Recommendations System
 * Sugere produtos complementares no carrinho baseado no último item adicionado
 */

(function() {
  'use strict';

  class CartRecommendations {
    constructor() {
      this.container = null;
      this.currentProductId = null;
      this.recommendations = [];
      this.isLoading = false;
      this.maxItems = 4;
      this.retryCount = 0;
      this.maxRetries = 2;
      this.init();
    }

    init() {
      this.injectContainer();
      this.bindEvents();
    }

    injectContainer() {
      const observer = new MutationObserver(() => {
        const drawer = document.querySelector('cart-items-component');
        if (drawer && !drawer.querySelector('[data-solfie-recommendations]')) {
          const summaryEl = drawer.querySelector('.cart-drawer__summary') ||
                           drawer.querySelector('[class*="summary"]');
          
          if (summaryEl) {
            this.container = document.createElement('div');
            this.container.setAttribute('data-solfie-recommendations', '');
            this.container.className = 'solfie-recommendations';
            summaryEl.parentNode.insertBefore(this.container, summaryEl);
          }
        }
      });

      observer.observe(document.body, { childList: true, subtree: true });
    }

    bindEvents() {
      document.addEventListener('cart:update', (e) => this.handleCartUpdate(e));
      document.addEventListener('product:added', (e) => this.handleProductAdded(e));
      document.addEventListener('theme:cart:update', (e) => this.handleCartUpdate(e));
      document.addEventListener('theme:product:added', (e) => this.handleProductAdded(e));
      this.interceptCartAdd();
    }

    interceptCartAdd() {
      const originalFetch = window.fetch;
      const self = this;

      window.fetch = function(...args) {
        const url = typeof args[0] === 'string' ? args[0] : args[0]?.url;
        
        if (url && (url.includes('/cart/add') || url.includes('/cart/change'))) {
          return originalFetch.apply(this, args).then(response => {
            if (response.ok) {
              response.clone().json().then(data => {
                if (url.includes('/cart/add')) {
                  const productId = data.items?.[0]?.product_id || data.product_id;
                  if (productId) {
                    self.fetchRecommendations(productId);
                  }
                }
              }).catch(() => {});
            }
            return response;
          });
        }
        
        return originalFetch.apply(this, args);
      };
    }

    handleCartUpdate(event) {
      const cart = event?.detail?.cart || event?.detail?.data;
      if (cart?.items?.length > 0) {
        const lastItem = cart.items[cart.items.length - 1];
        this.fetchRecommendations(lastItem.product_id);
      }
    }

    handleProductAdded(event) {
      const product = event?.detail?.product || event?.detail;
      if (product?.id) {
        this.fetchRecommendations(product.id);
      }
    }

    async fetchRecommendations(productId) {
      if (this.isLoading || productId === this.currentProductId) return;
      
      this.isLoading = true;
      this.currentProductId = productId;

      try {
        const response = await fetch(
          `/recommendations/products.json?product_id=${productId}&limit=${this.maxItems}&intent=related`,
          { headers: { 'X-Requested-With': 'XMLHttpRequest' } }
        );
        
        if (response.ok) {
          const data = await response.json();
          this.recommendations = data.products || [];
          if (this.recommendations.length > 0) {
            this.renderRecommendations(this.recommendations, 'Complete seu look');
          } else {
            this.renderFallback();
          }
        } else {
          this.renderFallback();
        }
      } catch (e) {
        console.warn('Cart recommendations: fetch error', e);
        this.renderFallback();
      } finally {
        this.isLoading = false;
      }
    }

    renderFallback() {
      if (!this.container) return;
      this.container.innerHTML = '';
    }

    renderRecommendations(products, title) {
      if (!this.container || !products?.length) {
        if (this.container) this.container.innerHTML = '';
        return;
      }

      const formatPrice = (price) => {
        return new Intl.NumberFormat('pt-BR', {
          style: 'currency',
          currency: 'BRL'
        }).format(price / 100);
      };

      const cardsHtml = products.map(product => {
        const variant = product.variants?.[0];
        const available = variant?.available !== false;
        const priceHtml = variant?.compare_at_price && variant.compare_at_price > variant.price
          ? `<span class="solfie-rec__price--compare">${formatPrice(variant.compare_at_price)}</span>
             <span class="solfie-rec__price--sale">${formatPrice(variant.price)}</span>`
          : `<span class="solfie-rec__price">${formatPrice(variant?.price || product.price)}</span>`;

        const badgeHtml = !available ? '<span class="solfie-rec__badge--soldout">Esgotado</span>' : '';

        return `
          <div class="solfie-rec__card" data-product-id="${product.id}">
            <a href="/products/${product.handle}" class="solfie-rec__link">
              <div class="solfie-rec__img-wrap">
                ${badgeHtml}
                <img 
                  src="${product.featured_image}&width=200&height=260&crop=center" 
                  alt="${product.title}"
                  class="solfie-rec__img"
                  loading="lazy"
                  width="200"
                  height="260"
                >
              </div>
              <div class="solfie-rec__info">
                <p class="solfie-rec__title">${product.title}</p>
                <div class="solfie-rec__prices">${priceHtml}</div>
              </div>
            </a>
            <button 
              class="solfie-rec__add-btn"
              data-variant-id="${variant?.id || ''}"
              data-product-id="${product.id}"
              ${!available ? 'disabled' : ''}
            >
              <span class="solfie-rec__add-text">${available ? 'Adicionar' : 'Indisponível'}</span>
              <span class="solfie-rec__add-check" style="display:none">✓</span>
            </button>
          </div>
        `;
      }).join('');

      this.container.innerHTML = `
        <div class="solfie-recommendations__header">
          <div class="solfie-recommendations__line"></div>
          <h4 class="solfie-recommendations__title">${title}</h4>
          <div class="solfie-recommendations__line"></div>
        </div>
        <div class="solfie-recommendations__scroll">
          <div class="solfie-recommendations__grid">
            ${cardsHtml}
          </div>
        </div>
      `;

      this.bindAddButtons();
    }

    bindAddButtons() {
      if (!this.container) return;

      this.container.querySelectorAll('.solfie-rec__add-btn').forEach(btn => {
        btn.addEventListener('click', async (e) => {
          e.preventDefault();
          e.stopPropagation();

          const variantId = btn.dataset.variantId;
          if (!variantId) return;

          const textEl = btn.querySelector('.solfie-rec__add-text');
          const checkEl = btn.querySelector('.solfie-rec__add-check');

          btn.disabled = true;
          btn.classList.add('solfie-rec__add-btn--loading');

          try {
            const response = await fetch('/cart/add.js', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'X-Requested-With': 'XMLHttpRequest'
              },
              body: JSON.stringify({ items: [{ id: parseInt(variantId), quantity: 1 }] })
            });

            if (response.ok) {
              textEl.style.display = 'none';
              checkEl.style.display = 'inline';
              btn.classList.remove('solfie-rec__add-btn--loading');
              btn.classList.add('solfie-rec__add-btn--success');

              document.dispatchEvent(new CustomEvent('cart:update', { bubbles: true }));

              setTimeout(() => {
                textEl.style.display = '';
                checkEl.style.display = 'none';
                btn.classList.remove('solfie-rec__add-btn--success');
                btn.disabled = false;
              }, 2000);
            } else {
              throw new Error('Failed to add');
            }
          } catch (err) {
            btn.classList.remove('solfie-rec__add-btn--loading');
            btn.disabled = false;
            console.warn('Cart recommendations: add failed', err);
          }
        });
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => new CartRecommendations());
  } else {
    new CartRecommendations();
  }

})();
